/**
 * Détection des contextes "magasin d'apps" pour respecter les politiques
 * d'Apple (App Store) et de Google (Play Store).
 *
 * Trois mondes possibles :
 *   1. Navigateur web (Safari/Chrome/...)            → Stripe disponible
 *   2. PWA installée via "Ajouter à l'écran d'accueil" → ancien mode iOS,
 *      on cache Stripe (Apple Guideline 3.1.1) car Apple voyait nos liens.
 *   3. App native Capacitor (iOS App Store / Android Play Store) →
 *      on utilise Apple IAP (RevenueCat). Stripe TOTALEMENT caché.
 *
 * Ces helpers ne dépendent QUE de signaux côté client présents au premier
 * rendu (navigator/document/window/Capacitor), donc sûrs à utiliser dans le JSX.
 */

import { Capacitor } from '@capacitor/core';

/** App native Capacitor (iOS ou Android wrappée). */
export function isCapacitorNative() {
  if (typeof window === 'undefined') return false;
  // 1. Try the official SDK helper (import path)
  try {
    if (Capacitor && typeof Capacitor.isNativePlatform === 'function' && Capacitor.isNativePlatform()) {
      return true;
    }
  } catch { /* ignored */ }
  // 2. Fallback to the global injected by the native runtime — this is
  //    the source of truth and works even if the module import was
  //    tree-shaken or the SDK helper threw.
  try {
    const w = window;
    if (w.Capacitor) {
      if (typeof w.Capacitor.isNativePlatform === 'function' && w.Capacitor.isNativePlatform()) return true;
      if (w.Capacitor.isNative === true) return true;
      if (w.Capacitor.platform && w.Capacitor.platform !== 'web') return true;
      if (typeof w.Capacitor.getPlatform === 'function' && w.Capacitor.getPlatform() !== 'web') return true;
    }
  } catch { /* ignored */ }
  return false;
}

/** App native iOS (Capacitor) — c'est ici qu'on appelle Apple IAP. */
export function isCapacitorIOS() {
  if (!isCapacitorNative()) return false;
  try {
    if (typeof Capacitor.getPlatform === 'function' && Capacitor.getPlatform() === 'ios') return true;
  } catch { /* ignored */ }
  try {
    const w = window;
    if (w.Capacitor) {
      if (typeof w.Capacitor.getPlatform === 'function' && w.Capacitor.getPlatform() === 'ios') return true;
      if (w.Capacitor.platform === 'ios') return true;
    }
  } catch { /* ignored */ }
  return false;
}

/** App native Android (Capacitor) — c'est ici qu'on appellera Google Play Billing. */
export function isCapacitorAndroid() {
  if (!isCapacitorNative()) return false;
  try {
    if (typeof Capacitor.getPlatform === 'function' && Capacitor.getPlatform() === 'android') return true;
  } catch { /* ignored */ }
  try {
    const w = window;
    if (w.Capacitor) {
      if (typeof w.Capacitor.getPlatform === 'function' && w.Capacitor.getPlatform() === 'android') return true;
      if (w.Capacitor.platform === 'android') return true;
    }
  } catch { /* ignored */ }
  return false;
}

/** Android TWA (Trusted Web Activity) — wrapped via PWABuilder/Bubblewrap. */
export function isPlayStoreApp() {
  if (typeof window === 'undefined') return false;
  if (typeof document !== 'undefined' && document.referrer && document.referrer.startsWith('android-app://')) {
    return true;
  }
  return false;
}

/**
 * iOS / iPadOS PWA installée sur l'écran d'accueil (LEGACY — version
 * PWABuilder non Capacitor). On masque dans ce contexte :
 *   - les références "Google Play"            (Guideline 2.3.10)
 *   - le login Google de tiers                (Guideline 4.8)
 *   - les flux d'achat Stripe                 (Guideline 3.1.1)
 */
export function isIOSStandaloneApp() {
  if (typeof window === 'undefined') return false;
  // Safari iOS / iPadOS expose un flag dédié.
  if (window.navigator && window.navigator.standalone === true) return true;
  // Filet de sécurité: iOS/iPadOS avec display-mode standalone (rare mais
  // possible sur les futures versions de WebKit).
  try {
    const ua = (window.navigator.userAgent || '').toLowerCase();
    const isAppleDevice = /iphone|ipad|ipod/.test(ua) ||
      (ua.includes('mac') && navigator.maxTouchPoints > 1); // iPadOS qui se déguise en Mac
    const isStandalone = window.matchMedia &&
      window.matchMedia('(display-mode: standalone)').matches;
    return isAppleDevice && isStandalone;
  } catch {
    return false;
  }
}

/**
 * Vrai si l'app tourne dans un contexte "magasin" SANS solution de paiement
 * intégrée (ancien PWABuilder iOS ou TWA Android sans IAP). Sert à cacher
 * Stripe ET à afficher le message "Visite le site web pour t'abonner".
 *
 * IMPORTANT : Sur Capacitor natif, on a RevenueCat IAP, donc cette fonction
 * retourne false → on affiche les boutons IAP normaux (pas le message web).
 */
export function isStoreApp() {
  return !isCapacitorNative() && (isIOSStandaloneApp() || isPlayStoreApp());
}

/**
 * Vrai si l'app tourne sur N'IMPORTE QUELLE plateforme magasin (Capacitor,
 * PWA standalone iOS, TWA Play Store). Utiliser pour cacher TOUS les liens
 * vers Stripe / Google Play, quel que soit le moyen de paiement.
 */
export function isAnyStoreContext() {
  return isCapacitorNative() || isIOSStandaloneApp() || isPlayStoreApp();
}

/**
 * @deprecated — Conservé pour compatibilité avec le code existant.
 * Aux nouveaux endroits, utiliser `isStoreApp()` (legacy sans IAP) ou
 * `isAnyStoreContext()` (toute app magasin), selon ce qu'on veut cacher.
 */
export function isInstalledApp() {
  return isStoreApp();
}

/** Origine web publique pour les liens client (signature, etc.). */
const PUBLIC_WEB_ORIGIN = 'https://soumissionpro.net';

/**
 * Sur Capacitor, `window.location.origin` est capacitor://localhost — inutilisable
 * pour un client. On force l'URL de production. Sur le web, on garde l'origine courante.
 */
export function getPublicWebOrigin() {
  if (typeof window === 'undefined') return PUBLIC_WEB_ORIGIN;
  if (isCapacitorNative()) return PUBLIC_WEB_ORIGIN;
  return window.location.origin || PUBLIC_WEB_ORIGIN;
}
